import { z } from "zod";

/**
 * The filter-system keys every list route's `validateSearch` must accept,
 * alongside its own `filters` value schema. Spread it the same way routes
 * spread `maskSearchFragment`:
 *
 *   const searchSchema = z.object({
 *     filters: z.object({ status: withOperators(z.string().optional()) }).optional(),
 *     ...filterSearchFragment,
 *     ...maskSearchFragment,
 *   });
 *
 * - `filtersOrder` — comma-joined filter keys, in the order their chips appear.
 *   Also holds the key of a chip added before a value is picked.
 *
 * The operator is NOT a separate key: it nests under the filter it belongs to
 * (`filters[status][notIn]=A`), matching the platform wire standard. Wrap each
 * field's value schema in `withOperators` to accept it — see `with-operators.ts`.
 */
export const filterSearchFragment = {
  filtersOrder: z.string().optional(),
};

/**
 * The `filters` keys the **notification inbox** writes, which every office route's
 * `validateSearch` must accept.
 *
 * Unlike `filterSearchFragment` and `maskSearchFragment`, this one is spread **inside the
 * `filters` object**, not at the top level — the inbox writes `filters[unread]` and
 * `filters[category]`:
 *
 *   filters: z.object({
 *     status: withOperators(z.string().optional()),
 *     ...notificationSearchFragment,
 *   }).optional(),
 *
 * **Why every office route needs it.** The notification popup lives in the office sidebar
 * (`features/office/sidebar/_components/nav-action.tsx`), so its unread toggle and category
 * chips can fire on ANY `/office/*` route. TanStack validates with the CURRENT route's schema
 * and `z.object` strips unknown keys, so a route that declares `filters` without these two
 * silently drops them: the toggle flips back, the chips do nothing, and the URL is rewritten
 * without the param. Same failure mode `maskSearchFragment` exists to prevent — see
 * `.claude/rules/webapp-routing.md`.
 *
 * A route whose schema declares no `filters` key at all does not need this.
 *
 * Loose strings on purpose: a `z.enum` of categories would throw `SearchParamError` and take
 * the page down on a stale link. Narrowing stays in the inbox, which owns the key names in
 * `features/office/notifications/_constants/notifications.ts` (`NOTIFICATION_QUERY_KEY`) —
 * keep the two in step.
 *
 * NOT wrapped in `withOperators`: these are read by `useQueryState`, not by the filter-chip
 * system, and the inbox never writes an operator-nested form.
 */
export const notificationSearchFragment = {
  unread: z.string().optional(),
  category: z.string().optional(),
  archived: z.string().optional(),
};
